import { IDashboardContract, UseContractsReturn } from "../../../../api/hooks/useContracts";
import {
  DataGrid,
  GridColumnVisibilityModel,
  GridInputRowSelectionModel,
  GridRowParams,
  useGridApiRef,
} from "@mui/x-data-grid";
import { getFilteredColDefs, NoRowsOverlay, useBreakpoint } from "@synota-io/synota-shared-ui";
import { startTransition, useCallback, useEffect, useState } from "react";
import { generatePath, useLocation } from "react-router-dom";
import { CONTRACTS_DETAIL_PATH } from "../../../../paths";
import { GridInitialStateCommunity } from "@mui/x-data-grid/models/gridStateCommunity";
import { useNavigateWithSearchState } from "../../../../shared/hooks/useNavigateWithSearchState";
import { DataGridToolbar } from "../../../../shared/datagrid/DataGridToolbar";
import { useContractFields } from "../hooks/useContractFields";

const initialState: GridInitialStateCommunity = {
  pagination: {
    paginationModel: {
      pageSize: 30,
    },
  },
  sorting: {
    sortModel: [{ field: "last_invoice", sort: "asc" }],
  },
};

export const ContractsTable = ({ contracts, isFetching, refetch }: UseContractsReturn) => {
  const isMedium = useBreakpoint("md");

  const navigate = useNavigateWithSearchState();
  const apiRef = useGridApiRef();

  const contractsColDef = getFilteredColDefs(useContractFields(), "table");

  const onRowDoubleClick = useCallback(
    (params: GridRowParams<IDashboardContract>) => {
      navigate(
        generatePath(CONTRACTS_DETAIL_PATH, {
          id: params.row.shared_uuid.toString(),
        }),
      );
    },
    [navigate],
  );

  const { state } = useLocation();

  const [rowSelectionModel, onRowSelectionModelChange] = useState<GridInputRowSelectionModel>([]);
  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>({});

  useEffect(() => {
    setColumnVisibility({
      begin_time: isMedium,
      end_time: isMedium,
      payment_method: isMedium,
      receive_method_name: isMedium,
    });
  }, [isMedium]);

  useEffect(() => {
    if (state && "selectedRow" in state && state.selectedRow) {
      refetch().finally(() => {
        setTimeout(() => {
          const allRows = apiRef.current.getSortedRows();
          const rowIndex = allRows.findIndex((row) => row.shared_uuid === state.selectedRow);
          if (rowIndex !== -1) {
            apiRef.current.scrollToIndexes({ rowIndex });
            startTransition(() => {
              onRowSelectionModelChange([state.selectedRow]);
            });
          }
        }, 250);
      });
    }
  }, [apiRef, refetch, state]);

  return (
    <DataGrid
      apiRef={apiRef}
      slots={{
        toolbar: DataGridToolbar,
        noResultsOverlay: NoRowsOverlay,
        noRowsOverlay: NoRowsOverlay,
      }}
      onRowDoubleClick={onRowDoubleClick}
      loading={!contracts?.length && isFetching}
      autosizeOptions={{
        expand: true,
        includeHeaders: true,
        includeOutliers: true,
      }}
      getRowId={(row) => row.shared_uuid}
      disableVirtualization
      columnVisibilityModel={columnVisibility}
      onColumnVisibilityModelChange={setColumnVisibility}
      onRowSelectionModelChange={onRowSelectionModelChange}
      rowSelectionModel={rowSelectionModel}
      sx={{ fontSize: "small" }}
      rows={contracts || []}
      columns={contractsColDef}
      pageSizeOptions={[30, 50, 100]}
      initialState={initialState}
    />
  );
};
